import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShoppingBag, Sparkles, ArrowRight, Instagram, Mail, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, assetUrl, formatApiError } from "@/lib/api";

export default function Storefront() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/products");
        setProducts(data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Unable to load products");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await api.post("/newsletter", { email });
      toast.success("You're on the list. Watch for our next drop.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Subscription failed");
    }
  };

  return (
    <div className="min-h-screen relative" data-testid="storefront-root">
      {/* Header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-baseline gap-2" data-testid="brand-link">
            <span className="font-serif-display text-2xl font-semibold tracking-wide">ATSHREE</span>
            <span className="overline hidden sm:inline">Heritage · Handcrafted</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <a href="#collection" className="hidden sm:inline text-stone-700 hover:text-stone-900" data-testid="nav-collection">Collection</a>
            <a href="#story" className="hidden sm:inline text-stone-700 hover:text-stone-900" data-testid="nav-story">Our Story</a>
            <Link to="/admin-login" className="text-stone-700 hover:text-stone-900" data-testid="nav-admin">
              Admin
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-14 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="md:col-span-6 fade-up">
            <div className="overline mb-6">Est. Karigar Atelier · Jaipur</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
              Heirlooms, <em className="italic text-[color:var(--ats-crimson)]">reimagined</em> for the modern woman.
            </h1>
            <p className="mt-6 text-stone-700 max-w-xl text-base leading-relaxed">
              Every piece at ATSHREE is shaped by master karigars over weeks — antique gold, kundan, meenakari, and pure Banarasi silk. Timeless. Never mass-made.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <a href="#collection">
                <Button data-testid="hero-cta-shop"
                  className="bg-[color:var(--ats-crimson)] hover:bg-[#5a0016] text-white rounded-full px-6 h-11">
                  Shop the Collection <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#story" className="text-sm font-medium underline underline-offset-4 decoration-[color:var(--ats-gold)]" data-testid="hero-cta-story">
                Meet the karigars
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-xs text-stone-600">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[color:var(--ats-emerald)]" /> Lifetime plating warranty</span>
              <span className="hidden sm:flex items-center gap-2"><Sparkles className="h-4 w-4 text-[color:var(--ats-gold-dark)]" /> 100% handcrafted</span>
            </div>
          </div>
          <div className="md:col-span-6 relative fade-up fade-up-2">
            <div className="relative aspect-[4/5] w-full rounded-lg overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1661621968475-f08ec1a768fc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
                alt="ATSHREE Heritage Editorial"
                className="absolute inset-0 h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 text-white">
                <div className="overline text-amber-300">Winter Drop 26</div>
                <div className="font-serif-display text-2xl mt-1">The Royal Karigar Edit</div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 hidden lg:block">
              <div className="bg-white shadow-xl rounded-md px-5 py-4 border border-stone-200">
                <div className="overline mb-1">Handcrafted in</div>
                <div className="font-serif-display text-lg">Jaipur, Rajasthan</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Grid */}
      <section id="collection" className="bg-[color:var(--ats-bg-alt)] py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="overline">The Collection</div>
              <h2 className="font-serif-display text-3xl lg:text-4xl mt-2">Signature Pieces</h2>
              <div className="gold-underline mt-3" />
            </div>
            <div className="hidden sm:block text-xs text-stone-600 font-mono-accent">
              {products.length} pieces · shipped worldwide
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="products-skeleton">
              {[0,1,2,3].map(i => (
                <div key={i} className="aspect-[3/4] rounded-md bg-stone-200 animate-pulse" />
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center text-stone-600 py-12" data-testid="products-empty">
              No products yet. Check back soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8" data-testid="products-grid">
              {products.map((p, idx) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="group block fade-up"
                  style={{ animationDelay: `${idx * 60}ms` }}
                  data-testid={`product-card-${idx}`}
                >
                  <div className="relative aspect-[3/4] overflow-hidden rounded-md bg-white border border-stone-200">
                    <img
                      src={assetUrl(p.image_url)}
                      alt={p.name}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    {p.compare_at_price && (
                      <div className="absolute top-3 left-3 bg-[color:var(--ats-crimson)] text-white px-2 py-1 text-[10px] font-mono-accent rounded">
                        {Math.round(((p.compare_at_price - p.price) / p.compare_at_price) * 100)}% OFF
                      </div>
                    )}
                  </div>
                  <div className="mt-4">
                    <div className="overline text-[10px]">{p.category}</div>
                    <div className="font-serif-display text-lg mt-1 leading-snug">{p.name}</div>
                    <div className="flex items-baseline gap-2 mt-1">
                      <span className="font-mono-accent text-sm">₹{Number(p.price).toLocaleString("en-IN")}</span>
                      {p.compare_at_price && (
                        <span className="font-mono-accent text-xs text-stone-500 line-through">
                          ₹{Number(p.compare_at_price).toLocaleString("en-IN")}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story strip */}
      <section id="story" className="relative py-20 lg:py-28 bg-[color:var(--ats-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <img
              src="https://images.unsplash.com/photo-1616756351484-798f37bdffa0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
              alt="Karigar at work"
              className="rounded-md w-full object-cover aspect-[4/5] shadow-lg"
            />
          </div>
          <div className="md:col-span-7">
            <div className="overline">Our Story</div>
            <h2 className="font-serif-display text-3xl lg:text-4xl mt-2 leading-snug">
              A quiet rebellion against mass-made luxury.
            </h2>
            <p className="mt-6 text-stone-700 leading-relaxed">
              ATSHREE partners with fourth-generation karigar families across Rajasthan and Varanasi. Every kundan setting, every zari thread, every meenakari petal is placed by hand. We take weeks where factories take minutes — because heirlooms deserve that much.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6">
              <div>
                <div className="font-serif-display text-3xl">40+</div>
                <div className="overline mt-1">Master karigars</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl">15d</div>
                <div className="overline mt-1">Avg. craft time</div>
              </div>
              <div>
                <div className="font-serif-display text-3xl">4.9★</div>
                <div className="overline mt-1">Customer rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="bg-[color:var(--ats-emerald)] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="overline text-amber-300">Newsletter</div>
            <h3 className="font-serif-display text-2xl lg:text-3xl mt-2">First access to new drops & private sales.</h3>
          </div>
          <form onSubmit={subscribe} className="flex gap-3" data-testid="newsletter-form">
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/60 h-12"
              data-testid="newsletter-email"
            />
            <Button type="submit" data-testid="newsletter-submit"
              className="bg-[color:var(--ats-gold)] text-slate-900 hover:bg-amber-400 h-12 rounded-md px-6">
              Subscribe
            </Button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-stone-950 text-stone-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-serif-display text-2xl text-white">ATSHREE</div>
            <div className="overline mt-1">Heritage · Handcrafted · Since 2019</div>
          </div>
          <div className="flex items-center gap-6 text-xs">
            <a href="mailto:hello@atshree.com" className="flex items-center gap-2 hover:text-white" data-testid="footer-email">
              <Mail className="h-4 w-4" /> hello@atshree.com
            </a>
            <a href="#" className="flex items-center gap-2 hover:text-white" data-testid="footer-instagram">
              <Instagram className="h-4 w-4" /> @atshree
            </a>
            <Link to="/admin-login" className="hover:text-white" data-testid="footer-admin">Admin</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
